import { Badge, Button, Icon, Link, Stack, Text } from "@chakra-ui/react"
import { createColumnHelper } from "@tanstack/react-table"
import { ConfirmationDialog } from "components/confirmation/confirmation"
import { ActionDrawer } from "components/drawer"
import { defaultColumns } from "components/table/defaultColumns"
import { remove } from "lib/firebase"
import LinkNames from "components/table/LinkNames"
import moment from "moment"
import { MdAirplay, MdDone, MdOutlineWebStories, MdPlaylistPlay, MdWarningAmber } from "react-icons/md"
import BroadcastModal from "views/modal/broadcast"
import GroupModal from "views/modal/group"
import GroupLocations from "views/modal/group-locations"

const helper = createColumnHelper()

export const columnsGroups = [
  ...defaultColumns,
  helper.display({
    id: 'broadcast',
    header: 'Broadcast',
    maxSize: 300,
    meta: { searchText: item => item.broadcast?.name || 'N/A' },
    cell: props => {
      const data = props.row.original
      if (data.broadcast) {
        return (
          <ActionDrawer
            title='Update broadcast'
            size={'xxl'}
            trigger={
              <Stack direction='row' spacing={2} align='center'>
                {data.broadcast.broadcastList ? (
                  <Icon as={MdOutlineWebStories} color={data.broadcast ? 'brand.500' : 'green'}  />
                ):(
                  <Icon as={MdAirplay} color={data.broadcast ? 'brand.500' : 'green'}   />
                )}
                <Link color='brand.500'>{data.broadcast.name}</Link>
              </Stack>
            }
            >
            <BroadcastModal broadcast={data.broadcast} onChange={props.table.options.context?.refresh} />
          </ActionDrawer>
        );
      }
      return 'N/A'
    },
  }),
  // The locations that belong to the group; a click opens the list to move more locations into it.
  // Not sortable (the list lives in the page context, not in the row).
  helper.display({
    id: 'locations',
    header: 'Locations',
    size: 240,
    meta: {
      searchText: (item, context) => {
        const list = context?.byGroup?.get(item.id) || []
        return list.length > 0 ? list.map(l => l.name).join(' ') : 'None'
      },
    },
    cell: props => {
      const group = props.row.original
      const list = props.table.options.context?.byGroup?.get(group.id) || []
      const names = list.map(l => l.name)
      return (
        <ActionDrawer
          title={'Locations of: ' + group.name}
          size='xl'
          trigger={<LinkNames names={names} none='No location in this group. Click to move some in.' />}
          >
          <GroupLocations group={group} onChange={props.table.options.context?.refresh} />
        </ActionDrawer>
      )
    },
  }),
  helper.accessor('layoutName', {
    header: 'Layout',
    size: 100,
    meta: { searchText: item => item.layoutName || 'N/A' },
    cell: props => {
      if (!props.row.original.layoutName) return 'N/A'
      return <Text>{props.row.original.layoutName}</Text>
    },
  }),
  helper.display({
    id: 'layout_color',
    header: 'Color',
    size: 100,
    meta: { searchText: item => item.layoutColor || 'N/A' },
    cell: props => {
      const color = props.row.original.layoutColor
      if (!color) return 'N/A'
      return <Stack direction='row' spacing={2}>
        <Badge fontSize='xs' backgroundColor={color}></Badge>
        <Text fontSize='xs'>{color}</Text>
      </Stack>
    },
  }),
  helper.display({
    id: 'actions',
    header: 'Actions',
    size: 180,
    cell: props => (
      <Stack direction='row' spacing={2} align='center'>
        <ActionDrawer
          title='Update group'
          size='xl'
          trigger={
            <Button colorScheme='brand' variant='ghost' size='xs'>
              Update
            </Button>
          }
          >
          <GroupModal group={props.row.original} onChange={props.table.options.context?.refresh} />
        </ActionDrawer>
        <ActionDrawer
          title={'Copy group: ' + props.row.original.name}
          size='2xl'
          trigger={
            <Button colorScheme='brand' variant='ghost' size='xs'>
              Copy
            </Button>
          }
          >
          <GroupModal template={props.row.original} onChange={props.table.options.context?.refresh} />
        </ActionDrawer>
        <ConfirmationDialog
          title='Delete group'
          message='Are you sure you want to delete this group?'
          confirmLabel='Delete'
          trigger={
            <Button colorScheme='red' variant='ghost' size='xs'>
              Delete
            </Button>
          }
          onConfirm={async () => {
            await remove('group', props.row.original.id)
            props.table.options.context?.refresh()
          }}
          />
      </Stack>
    ),
  }),
]